import React, {useState} from "react";
import history from "shared_history";
import queryString from "query-string";
import moment from 'moment';
import PropTypes from 'prop-types';

// derived from SchoolAdmin::DateRange::DATE_RANGE_OPTIONS
export const DATE_RANGES = {
  yesterday: 'yesterday',
  tomorrow: 'tomorrow',
  today: 'today',
  last_week: 'last_week',
  next_week: 'next_week',
  this_week: 'this_week',
  next_7_days: 'next_7_days',
  this_month: 'this_month',
  last_month: 'last_month',
  last_7_days: 'last_7_days',
  last_30_days: 'last_30_days',
  last_60_days: 'last_60_days',
  last_90_days: 'last_90_days',
  custom: 'custom'
}

export const pastOptions = [
  ['Yesterday', DATE_RANGES.yesterday],
  ['Today', DATE_RANGES.today],
  ['Last Week', DATE_RANGES.last_week],
  ['This Week', DATE_RANGES.this_week],
  ['This Month', DATE_RANGES.this_month],
  ['Last Month', DATE_RANGES.last_month],
  ['Last 7 Days', DATE_RANGES.last_7_days],
  ['Last 30 Days', DATE_RANGES.last_30_days],
  ['Last 60 Days', DATE_RANGES.last_60_days],
  ['Last 90 Days', DATE_RANGES.last_90_days],
  ['Custom', DATE_RANGES.custom]
];

export const futureOptions = [
  ['Tomorrow', DATE_RANGES.tomorrow],
  ['Next Week', DATE_RANGES.next_week],
  ['Next 7 Days', DATE_RANGES.next_7_days]
];

export const allOption = [['All', 'all']];

const withDateFilter = ({
  defaultRange = DATE_RANGES.last_7_days
} = {}) => (WrappedComponent) => {
  const DateFilteredComponent = (props) => {
    const query = queryString.parse(location.search);

    const urlRange = query['range'] || defaultRange;
    const urlFrom = query['from'] ? moment(query['from']).toDate() : undefined;
    const urlTo = query['to'] ? moment(query['to']).toDate() : undefined;

    const [filter, setFilterState] = useState(urlRange);
    const [from, setFrom] = useState(urlFrom);
    const [to, setTo] = useState(urlTo);

    const setFilter = (filter) => {
      setFilterState(filter);

      if (filter !== 'custom') {
        applyFilter(filter);
      }
    };

    const applyFilter = (newRange) => {
      const query = queryString.parse(location.search);

      // remove the pagination data for both regular and cursor pagination from the query
      // so that setting the filter bounces the user back to the first page
      const {page, startingAfter, endingBefore, range, ...unpaginatedQuery} = query;

      const oldParams = filterParams(range, urlFrom, urlTo);
      const newParams = filterParams(newRange, from, to);

      if (oldParams !== newParams) {
        history.push({
          ...location,
          search: `?${queryString.stringify({
            ...unpaginatedQuery,
            ...newParams
          })}`
        });
      }
    }

    const filterParams = (range, from, to) => {
      let ret = {range};

      if (range === 'custom') {
        ret['from'] = moment(from).format('YYYY-MM-DD')
        ret['to'] = moment(to).format('YYYY-MM-DD')
      }

      return ret;
    };

    const clear = () => {
      setFilter(defaultRange);
    };

    return <WrappedComponent
      {...props}
      dateFilter={{
        filter,
        from,
        to,
        setFilterState,
        setFrom,
        setTo,
        applyFilter,
        setFilter,
        clear,
        queryParams: filterParams(urlRange, urlFrom, urlTo)
      }}
    />;
  };

  DateFilteredComponent.propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string
    }).isRequired
  };

  WrappedComponent.propTypes = {
    ...(WrappedComponent.propTypes || {}),
    dateFilter: PropTypes.shape({
      filter: PropTypes.oneOf(['all', ...Object.values(DATE_RANGES)]), // range to filter by
      from: PropTypes.object, // start date
      to: PropTypes.object, // end date
      setFrom: PropTypes.func,
      setTo: PropTypes.func,
      setFilter: PropTypes.func, // Sets the current selected filter without necessarily applying it (i.e. in case we need to present extra custom options)
      applyFilter: PropTypes.func, // Applies the currently selected filter
      clear: PropTypes.func // Reset to default filter
    })
  }

  return DateFilteredComponent;
}

export default withDateFilter;
